import { Alert, Button, Card, Col, FormCheck, Row } from 'react-bootstrap';
import { useExtendOrder } from 'pages/orders/extend_order/context';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatDay } from 'utils';

type DetailItemProps = {
    highlight?: boolean;
    label: string;
    value: string | number | null | undefined;
};

const DetailItem = (props: DetailItemProps) => {

    const { highlight = false, label, value } = props;

    return (
        <div>
            <dt className="text-muted small mb-1">
                {label}
            </dt>
            <dd className={`mb-0${highlight ? ' text-warning fw-semibold' : ''}`}>
                {value ?? '—'}
            </dd>
        </div>
    );

};

export const ReviewStep = props => {

    const { confirmed, expireDays, extendedBy, handleBack, handleChangeConfirmed, handleConfirm, newExpireDate, newStartDate,
        requestedBy, requiresConfirmation, startDays } = useExtendOrder();
    const intl = useIntl();
    const { formatMessage } = intl;

    const formatDate = (date: Date | null) => formatDay(date, intl);

    const confirmDisabled = requiresConfirmation && !confirmed;

    return (
        <div className={'review-step d-flex flex-column gap-3'}>
            <h6>
                <FormattedMessage defaultMessage="Please review the changes below before submitting" id="please_review_the_changes_below_before_s.cd5baa69" />
            </h6>

            <Card>
                <Card.Body className={'d-flex flex-column gap-3'}>
                    <dl className="mb-0">
                        <Row className={'gy-3'}>
                            <Col className={'d-flex flex-column gap-3'} sm={4}>
                                <DetailItem
                                    label={formatMessage({ id: 'extended_by.3a058bcc', defaultMessage: 'Extended by' })}
                                    value={extendedBy}
                                />
                                <DetailItem
                                    label={formatMessage({ id: 'per_request_of.a1b165a2', defaultMessage: 'Per request of' })}
                                    value={requestedBy}
                                />
                            </Col>
                            <Col className={'d-flex flex-column gap-3'} sm={4}>
                                <DetailItem
                                    label={formatMessage({ id: 'new_start_date.3edf96b8', defaultMessage: 'New start date' })}
                                    value={formatDate(newStartDate)}
                                />
                                <DetailItem
                                    highlight={requiresConfirmation && startDays !== 0}
                                    label={formatMessage({ id: 'start_date_adjustment.69823ad4', defaultMessage: 'Start date adjustment' })}
                                    value={startDays === 0 ? '—' : `${startDays > 0 ? '+' : ''}${startDays} days`}
                                />
                            </Col>
                            <Col className={'d-flex flex-column gap-3'} sm={4}>
                                <DetailItem
                                    highlight={requiresConfirmation && expireDays !== 0}
                                    label={formatMessage({ id: 'new_expiration_date.c297bbee', defaultMessage: 'New expiration date' })}
                                    value={formatDate(newExpireDate)}
                                />
                                <DetailItem
                                    highlight={requiresConfirmation && expireDays !== 0}
                                    label={formatMessage({ id: 'expiration_date_adjustment.82139355', defaultMessage: 'Expiration date adjustment' })}
                                    value={expireDays === 0 ? '—' : `${expireDays > 0 ? '+' : ''}${expireDays} days`}
                                />
                            </Col>
                        </Row>
                    </dl>

                    {requiresConfirmation && (
                        <Alert show={true} variant={'warning'}>
                            <Alert.Heading>
                                <FormattedMessage defaultMessage="Warning" id="warning.64b35335" />
                                :
                            </Alert.Heading>

                            <p>
                                <FormattedMessage defaultMessage="One or more of the date adjustments is outside the normal range. Do you want to proceed.?" id="one_or_more_of_the_date_adjustments_is_o.fad8fcff" />
                            </p>

                            <FormCheck
                                checked={confirmed}
                                className={'gap-1 fw-semibold'}
                                label={formatMessage({ id: 'i_understand_and_want_to_proceed_with_th.8f5ad621', defaultMessage: 'I understand and want to proceed with these changes.' })}
                                onChange={e => handleChangeConfirmed(e.target.checked)}
                            />
                        </Alert>
                    )}

                    <div className={'d-flex justify-content-end gap-2'}>
                        <Button onClick={handleBack} variant="secondary">
                            <FormattedMessage defaultMessage="Back" id="back.b6238c9e" />
                        </Button>
                        <Button disabled={confirmDisabled} onClick={handleConfirm}>
                            <FormattedMessage defaultMessage="Submit" id="submit.7d363fed" />
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );

};
